/**
 * Robocall blocker — the shapes returned by /callcenter/admin/robocall-blocks.
 *
 * Vocabulary is deliberately the same as the CRM's and the analytics dashboard's, so the
 * three cannot drift. Never call an unconfirmed number a robocall: a number that only
 * matches the signature is a POTENTIAL robocall (`candidate`).
 */

export type BlockStatus = 'candidate' | 'active' | 'expired' | 'released'
export type BlockSource = 'auto' | 'manual' | 'imported' | 'agent'

/** Machine evidence that produced the score. Everything here is optional: rows created
 *  manually carry only `trigger`. */
export class Evidence {
    distinct_businesses?: number
    distinct_locations?: number
    /** Median handle time in seconds, over `duration_samples` answered calls. */
    median_handle_s?: number | null
    calls?: number
    /** Calls an agent actually picked up. */
    answered_calls?: number
    /** Answered calls that carry a usable duration — the median rests on these. */
    duration_samples?: number
    max_duration_s?: number
    /** Calls an agent talked on for longer than the veto threshold. */
    long_convos?: number
    strong_spam_tags?: number
    weak_spam_tags?: number
    lead_tags?: number
    /** Pieces of real-lead evidence: 0 none, 1 weak, 2+ terminal. */
    veto_strength?: number
    window?: { hours?: number, until?: string }
    businesses_sample?: string[]
    score_breakdown?: { [component: string]: number }
    trigger?: 'scheduled' | 'agent' | 'manual'
    last_agent_signal?: { user_id?: number | null, call_id?: number | null, at?: string }
}

/**
 * The `action` JSON on a history row: always an action name and a comment, plus whatever
 * context that particular transition recorded — veto_strength, term_hours, call_sid and so
 * on. Declared as an interface rather than a class precisely because of that open tail.
 */
export interface HistoryAction {
    action: string
    comment?: string
    [key: string]: any
}

export class BlockHistory {
    id: number = 0
    blocked_number_id: number = 0
    user_id?: number | null = null
    action?: HistoryAction
    created_at: string = ''
    user?: { id: number, email: string } | null
}

export class BlockedNumber {
    id?: number
    phone_e164: string = ''
    phone_key: string = ''
    status: BlockStatus = 'candidate'
    source: BlockSource = 'auto'
    score: number = 0
    evidence?: Evidence | null
    blocked_at?: string | null
    expires_at?: string | null
    released_at?: string | null
    released_by?: number | null
    created_by?: number | null
    hit_count: number = 0
    last_hit_at?: string | null
    /** Terminal veto. A weak veto (veto_strength === 1) does NOT set this. */
    veto_protected: boolean = false
    veto_strength: number = 0
    block_count: number = 0
    veto_at?: string | null
    created_at?: string
    updated_at?: string
    history?: BlockHistory[]
    creator?: { id: number, email: string } | null
    releaser?: { id: number, email: string } | null
}

/** GET /robocall-blocks/{phone} also reports whether the block is live in Redis right
 *  now, which the row alone cannot tell you. */
export class BlockDetail {
    number: BlockedNumber = new BlockedNumber()
    enforcement: {
        live_in_cache: boolean
        cache_payload: { id: number, expires_at: string | null } | null
        seconds_remaining: number | null
    } = { live_in_cache: false, cache_payload: null, seconds_remaining: null }
}

export class BulkResult {
    action: string = ''
    requested: number = 0
    applied: number = 0
    skipped: { id: number, reason: string }[] = []
}

export interface IBlockFilters {
    status?: BlockStatus | ''
    source?: BlockSource | ''
    q?: string
    min_score?: number | ''
    /** Numbers whose signature contradicts their veto. Only a person can resolve these —
     *  automation may never touch them — so without this filter they are invisible. */
    veto_conflicts?: boolean
}

/**
 * Weak veto = exactly one piece of real-lead evidence. It stops automatic promotion but
 * leaves the number reviewable; a reviewer may still confirm it.
 */
export const isWeakVeto = (n: BlockedNumber): boolean =>
    n.veto_strength === 1 && !n.veto_protected

export const isTerminalVeto = (n: BlockedNumber): boolean =>
    n.veto_protected || n.veto_strength >= 2

/** Human label for a status, matching the analytics dashboard's wording. */
export const statusLabel = (status: BlockStatus): string => {
    switch (status) {
        case 'candidate': return 'Potential'
        case 'active': return 'Confirmed'
        case 'released': return 'Cleared'
        case 'expired': return 'Expired'
        default: return status
    }
}

/* ---------------------------------------------------------------------------
 * Settings
 * ------------------------------------------------------------------------- */

export interface ISettingsField {
    type: 'bool' | 'int'
    default: number
    min?: number
    max?: number
    group: string
    label: string
    help: string
}

export interface IReasonGroup {
    label: string
    allow_empty: boolean
    help: string
}

export interface ILoseCallReason {
    id: number
    reason: string
    active: number
}

/** A reason id in the map that cannot do its job — missing, or deactivated. */
export interface ISettingsProblem {
    group: string
    id: number
    severity: 'error' | 'warning' | 'info'
    message: string
}

export interface ISettingsHistoryRow {
    id: number
    user_id: number | null
    changes: { [key: string]: { from: any, to: any } }
    comment: string
    created_at: string
    user?: { id: number, email: string } | null
}

/**
 * What the three switches add up to. Computed server-side so this screen cannot describe
 * the state differently from the code that acts on it.
 */
export interface IEffectiveState {
    state: 'off' | 'dry_run' | 'live'
    label: string
    detail: string
}

/**
 * Spam-attack mode: a named set of values a reviewer can load with one click.
 *
 * `values` is a partial — a preset names only the settings that matter under attack and
 * leaves the rest of the form alone. Loading one never saves: it fills the form so the
 * change count and the required comment still apply, which is what keeps every change
 * going through the same validation and the same history.
 */
export interface ISettingsPreset {
    label: string
    help: string
    values: { [key: string]: number }
}

/**
 * One detection rule, switchable on its own.
 *
 * A preset moves several settings at once, which is right at the start of a wave and wrong
 * while working out whether a single rule earns its place — three changes arriving together
 * cannot be told apart afterwards. Keyed by the boolean setting the switch flips.
 */
export interface IRuleToggle {
    label: string
    help: string
}

export interface ISettingsResponse {
    values: { [key: string]: number | boolean }
    reason_ids: { [group: string]: number[] }
    schema: {
        fields: { [key: string]: ISettingsField }
        groups: { [key: string]: string }
        reason_groups: { [key: string]: IReasonGroup }
        presets: { [name: string]: ISettingsPreset }
        rule_toggles: { [key: string]: IRuleToggle }
    }
    reasons: ILoseCallReason[]
    /**
     * Ids of classifications an operator can no longer pick. They stay in `reasons` and stay
     * selectable — one already sitting in an id map has to remain visible and removable —
     * but the screen collapses them so they do not bury the dozen actually in use.
     */
    reasons_inactive_ids: number[]
    problems: ISettingsProblem[]
    effective_state: IEffectiveState
    history: ISettingsHistoryRow[]
    unknown_keys: string[]
}
